import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";

const ROLES = ["owner", "admin", "driver", "finance", "partner", "pending"];
const ASSIGNABLE = ["admin", "driver", "finance", "partner", "owner"];
const DESC = {
  owner: "Akses penuh: sistem, keuangan, operasional, dan staf",
  admin: "Operasional: booking, kalender, aset, scan, dan inspeksi",
  driver: "Booking miliknya + upload foto/video antar & ambil",
  finance: "Keuangan: pembayaran, deposit, komisi, dan laporan",
  partner: "Hanya Hub-nya: Booking di Venue + komisi",
  pending: "Tidak ada akses — dicabut / menunggu persetujuan",
};

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin", hub_branch_ids: [], commission_percent: 0 });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [catalog, setCatalog] = useState([]);
  const [branches, setBranches] = useState([]);
  const [openPerms, setOpenPerms] = useState("");
  const [draft, setDraft] = useState([]);
  const [notifCats, setNotifCats] = useState([]);
  const [openNotif, setOpenNotif] = useState("");
  const [notifDraft, setNotifDraft] = useState({});

  const load = () => api.get("/users").then((r) => setRows(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || "Not allowed"));
  useEffect(() => {
    load();
    api.get("/perms-catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => setBranches([]));
    api.get("/notification-categories").then((r) => setNotifCats(r.data)).catch(() => {});
  }, []);

  const act = async (fn, okMsg) => {
    try { await fn(); load(); toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const create = () => act(async () => {
    await api.post("/users", form);
    setForm({ name: "", email: "", password: "", role: "admin", hub_branch_ids: [], commission_percent: 0 });
  }, "Akun staf dibuat");

  const resetPassword = (u) => {
    const p = window.prompt(`Password baru untuk ${u.email} (min 8 karakter):`);
    if (!p) return;
    act(() => api.put(`/users/${u.user_id}/password`, { password: p }), "Password direset");
  };

  const changeMyPassword = () => act(async () => {
    await api.post("/auth/change-password", pw);
    setPw({ current_password: "", new_password: "" });
  }, "Password Anda diperbarui");

  const isOwner = user?.role === "owner";

  const togglePerms = (u) => {
    if (openPerms === u.user_id) return setOpenPerms("");
    setDraft(u.perms || []);
    setOpenPerms(u.user_id);
  };
  const savePerms = (u) => act(async () => {
    await api.put(`/users/${u.user_id}/perms`, { perms: draft });
    setOpenPerms("");
  }, `Izin tambahan ${u.email} disimpan`);
  const groups = [...new Set(catalog.map((c) => c.group))];

  const toggleNotif = (u) => {
    if (openNotif === u.user_id) return setOpenNotif("");
    setNotifDraft({
      notify_email: u.notify_email || "", notify_enabled: u.notify_enabled !== false,
      notify_digest: !!u.notify_digest, notify_muted: u.notify_muted || [],
    });
    setOpenNotif(u.user_id);
  };
  const saveNotif = (u) => act(async () => {
    await api.put(`/users/${u.user_id}/notifications`, notifDraft);
    setOpenNotif("");
  }, `Notifikasi ${u.email} disimpan`);

  return (
    <div data-testid="admin-users" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Staff Accounts</h1>
        <p className="mt-2 text-sm text-ink-soft max-w-2xl">
          Staf masuk di <span className="text-ink">/admin/login</span> dengan email &amp; password.
          Hanya <span className="text-ink">owner</span> yang bisa memberi akses, mencabut,
          reset password, dan menghapus akun. Setiap staf bisa mengganti password sendiri di bawah.
        </p>
      </div>

      {isOwner && (
        <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="create-staff">
          <h2 className="text-base font-semibold">Buat Akun Staf Baru</h2>
          <div className="mt-3 grid sm:grid-cols-4 gap-3">
            <Input data-testid="new-staff-name" placeholder="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="new-staff-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="new-staff-password" placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <select data-testid="new-staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="h-10 rounded-md border border-input bg-white px-2 text-sm">
              {ASSIGNABLE.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {["admin", "partner"].includes(form.role) && (
            <div className="mt-3" data-testid="new-staff-hub-fields">
              <div className="text-[11px] uppercase tracking-widest text-ink-soft">Hub yang boleh diakses</div>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {branches.map((b) => {
                  const on = (form.hub_branch_ids || []).includes(b.id);
                  return (
                    <button key={b.id} type="button" data-testid={`new-staff-hub-${b.code}`}
                      onClick={() => setForm({ ...form, hub_branch_ids: on
                        ? form.hub_branch_ids.filter((x) => x !== b.id)
                        : [...(form.hub_branch_ids || []), b.id] })}
                      className={`px-3 py-1.5 rounded-full text-xs border ${on ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
                      {b.name}
                    </button>
                  );
                })}
              </div>
              {form.role === "partner" && (
                <Input data-testid="new-staff-commission" type="number" className="mt-2 max-w-xs"
                  placeholder="Komisi % (kosong = ikut Hub)" value={form.commission_percent}
                  onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} />
              )}
            </div>
          )}
          <button data-testid="create-staff-btn" onClick={create}
            className="mt-3 lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">+ Buat Akun</button>
        </section>
      )}

      <div className="overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Nama</th><th className="p-3">Email</th><th className="p-3">Role</th>
              <th className="p-3">Akses</th><th className="p-3">Aksi</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((u) => (
              <React.Fragment key={u.user_id}>
              <tr data-testid={`user-row-${u.email}`}>
                <td className="p-3">{u.name || "—"}{u.user_id === user?.user_id && <span className="ml-2 text-[10px] text-lm-red">you</span>}</td>
                <td className="p-3">{u.email}<div className="text-[10px] text-ink-soft">{u.has_password ? "password aktif" : "belum mengatur password"}</div></td>
                <td className="p-3">
                  <select data-testid={`role-select-${u.email}`} className="h-9 rounded-md border border-input bg-white px-2 text-sm"
                    value={u.role} disabled={!isOwner || u.user_id === user?.user_id || u.role === "owner"}
                    onChange={(e) => act(() => api.put(`/users/${u.user_id}`, { role: e.target.value }), `${u.email} sekarang ${e.target.value}`)}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="p-3 text-xs text-ink-soft">{DESC[u.role]}
                  {["admin", "partner"].includes(u.role) && isOwner && (
                    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`hub-access-${u.email}`}>
                      {branches.map((b) => {
                        const on = (u.hub_branch_ids || []).includes(b.id);
                        return (
                          <button key={b.id} type="button" data-testid={`hub-toggle-${u.email}-${b.code}`}
                            onClick={() => act(() => api.put(`/users/${u.user_id}`, {
                              role: u.role,
                              hub_branch_ids: on ? (u.hub_branch_ids || []).filter((x) => x !== b.id)
                                                 : [...(u.hub_branch_ids || []), b.id],
                            }), `Hub ${u.email} diperbarui`)}
                            className={`px-2 py-0.5 rounded-full text-[11px] border ${on ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
                            {b.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {(u.perms || []).length > 0 && (
                    <div className="mt-1 text-[10px] text-lm-red" data-testid={`perm-count-${u.email}`}>
                      +{u.perms.length} izin tambahan
                    </div>
                  )}
                </td>
                <td className="p-3">
                  {isOwner && (
                    <div className="flex flex-wrap gap-3 text-xs">
                      {u.role !== "owner" && u.user_id !== user?.user_id && (
                        <button data-testid={`edit-perms-${u.email}`} onClick={() => togglePerms(u)}
                          className="text-ink hover:underline">{openPerms === u.user_id ? "Tutup akses" : "Checklist akses"}</button>
                      )}
                      <button data-testid={`edit-notif-${u.email}`} onClick={() => toggleNotif(u)}
                        className="text-ink hover:underline">{openNotif === u.user_id ? "Tutup notifikasi" : "Notifikasi"}</button>
                      {u.user_id !== user?.user_id && (
                        <button data-testid={`reset-pw-${u.email}`} onClick={() => resetPassword(u)} className="text-ink hover:underline">Reset password</button>
                      )}
                      {u.role !== "owner" && u.role !== "pending" && u.user_id !== user?.user_id && (
                        <button data-testid={`revoke-${u.email}`}
                          onClick={() => window.confirm(`Cabut akses ${u.email}?`) && act(() => api.post(`/users/${u.user_id}/revoke`), "Akses dicabut")}
                          className="text-amber-700 hover:underline">Cabut akses</button>
                      )}
                      {u.role !== "owner" && u.user_id !== user?.user_id && (
                        <button data-testid={`delete-user-${u.email}`}
                          onClick={() => window.confirm(`Hapus permanen akun ${u.email}?`) && act(() => api.delete(`/users/${u.user_id}?hard=true`), "Akun dihapus")}
                          className="text-lm-red hover:underline">Hapus</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
              {openPerms === u.user_id && (
                <tr className="bg-cream-dark/40" data-testid={`perms-panel-${u.email}`}>
                  <td colSpan={5} className="p-4">
                    <div className="text-xs text-ink-soft">
                      Role <span className="text-ink">{u.role}</span> memberi akses dasar. Centang akses
                      tambahan yang diperlukan; menu staf akan muncul otomatis setelah disimpan.
                    </div>
                    <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                      {groups.map((g) => (
                        <div key={g}>
                          <div className="text-[10px] uppercase tracking-widest text-ink-soft">{g}</div>
                          <div className="mt-1.5 space-y-1.5">
                            {catalog.filter((c) => c.group === g).map((c) => (
                              <label key={c.action} className="flex items-start gap-2 text-xs cursor-pointer">
                                <input type="checkbox" data-testid={`perm-${u.email}-${c.action}`}
                                  className="mt-0.5 accent-lm-red"
                                  checked={draft.includes(c.action)}
                                  onChange={(e) => setDraft(e.target.checked
                                    ? [...draft, c.action]
                                    : draft.filter((x) => x !== c.action))} />
                                <span>{c.label}<span className="block text-[10px] text-ink-soft">{c.action}</span></span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-3">
                      <button data-testid={`save-perms-${u.email}`} onClick={() => savePerms(u)}
                        className="lm-btn-primary rounded-md px-4 py-2 text-sm font-semibold">Simpan Izin</button>
                      <button data-testid={`clear-perms-${u.email}`} onClick={() => setDraft([])}
                        className="rounded-md px-4 py-2 text-sm border border-black/15">Kosongkan</button>
                    </div>
                  </td>
                </tr>
              )}
              {openNotif === u.user_id && (
                <tr className="bg-cream-dark/40" data-testid={`notif-panel-${u.email}`}>
                  <td colSpan={5} className="p-4">
                    <div className="text-xs text-ink-soft">
                      Email notifikasi operasional untuk <span className="text-ink">{u.email}</span>.
                      Kategori penting (booking berbayar, telat kembali, kerusakan, masalah pembayaran,
                      pembatalan, konflik Hub) tetap terkirim walau dimatikan.
                    </div>
                    <div className="mt-3 grid sm:grid-cols-3 gap-3">
                      <Input data-testid={`notif-email-${u.email}`} placeholder={`Alamat lain (default ${u.email})`}
                        value={notifDraft.notify_email || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, notify_email: e.target.value })} />
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" className="accent-lm-red" data-testid={`notif-enabled-${u.email}`}
                          checked={notifDraft.notify_enabled !== false}
                          onChange={(e) => setNotifDraft({ ...notifDraft, notify_enabled: e.target.checked })} />
                        Kirim email ke staf ini
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" className="accent-lm-red" data-testid={`notif-digest-${u.email}`}
                          checked={!!notifDraft.notify_digest}
                          onChange={(e) => setNotifDraft({ ...notifDraft, notify_digest: e.target.checked })} />
                        Gabung jadi ringkasan pagi 08:00 WIB
                      </label>
                    </div>
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-widest text-ink-soft">Matikan kategori</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {notifCats.map((c) => {
                          const off = (notifDraft.notify_muted || []).includes(c.key);
                          return (
                            <button key={c.key} type="button" data-testid={`notif-mute-${u.email}-${c.key}`}
                              disabled={c.critical}
                              onClick={() => setNotifDraft({ ...notifDraft, notify_muted: off
                                ? notifDraft.notify_muted.filter((x) => x !== c.key)
                                : [...(notifDraft.notify_muted || []), c.key] })}
                              className={`px-2.5 py-1 rounded-full text-[11px] border ${
                                off ? "bg-ink text-cream border-ink" : "border-black/15"} ${
                                c.critical ? "opacity-40 cursor-not-allowed" : ""}`}>
                              {c.label}{c.critical ? " (wajib)" : ""}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <button data-testid={`save-notif-${u.email}`} onClick={() => saveNotif(u)}
                      className="mt-4 lm-btn-primary rounded-md px-4 py-2 text-sm font-semibold">Simpan Notifikasi</button>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-5 text-ink-soft">Belum ada akun staf.</td></tr>}
          </tbody>
        </table>
      </div>

      <section className="bg-white border border-black/10 rounded-lg p-5 max-w-md" data-testid="my-password">
        <h2 className="text-base font-semibold">Ganti Password Saya</h2>
        <div className="mt-3 space-y-3">
          <Input data-testid="my-current-password" type="password" placeholder="Password saat ini"
            value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          <Input data-testid="my-new-password" type="password" placeholder="Password baru (min 8)"
            value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <button data-testid="my-password-btn" onClick={changeMyPassword}
            className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">Simpan Password</button>
        </div>
      </section>
    </div>
  );
}
